import { dependencies } from '@pn/core/dependencies';
import { useWorkspaceStorage } from '@pn/core/storage';
import { isNil } from 'lodash-es';
import React from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

/**
 * Append ?hideAllButSelected=1 to the URL that selects a single data item.
 * E.g. /wells/100093302503W500?hideAllButSelected=1
 */
export function useHideAllButSelected() {
  const { pathname, search } = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const hideAllButSelected = searchParams.get('hideAllButSelected') === '1';

  const { workspaceItems } = useWorkspaceStorage();

  const [initialPathname] = React.useState(pathname);

  React.useEffect(() => {
    if (!hideAllButSelected || isNil(id)) return;

    const { map } = dependencies;
    const filteredLayerIds: string[] = [];

    Object.values(workspaceItems)
      .filter(({ isRendered, module }) => isRendered && isNil(module))
      .forEach((item) => {
        item.map.layers.forEach((layer) => {
          filteredLayerIds.push(layer.id);
          map.filterLayer({
            layerId: layer.id,
            key: 'hide_all_but_selected',
            filter: ['==', 'internal_id', id],
          });
        });
      });

    return () => {
      filteredLayerIds.forEach((layerId) => {
        map.filterLayer({
          layerId,
          key: 'hide_all_but_selected',
          filter: undefined,
        });
      });
    };
  }, [id, hideAllButSelected, workspaceItems]);

  React.useEffect(() => {
    if (!hideAllButSelected || pathname === initialPathname) return;

    const nextParams = new URLSearchParams(search);
    nextParams.delete('hideAllButSelected');

    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [hideAllButSelected, pathname, initialPathname, search, navigate]);
}
